import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  ArrowRight, 
  CheckCircle, 
  Award, 
  Users, 
  Globe, 
  Star,
  Factory,
  Wrench,
  TrendingUp,
  ShieldCheck,
  MessageCircle
} from "lucide-react";
import { products, whyChooseUs, testimonials, companyInfo, globalPresence } from "../data/mock";
import { WorldMapDetailed } from "../components/WorldMapDetailed";

export const Home = () => {
  const featuredProducts = products.slice(0, 4);

  return (
    <>
      <Helmet>
        <title>BOPP Tape Coating Machine Manufacturer in India Since 1977 | Hero Impex</title>
        <meta name="description" content="Leading manufacturer of BOPP tape coating machines, slitter rewinders, and converting machinery in India. 800+ clients across 20+ countries. German quality standards since 1977. Get instant quote!" />
        <meta name="keywords" content="BOPP tape coating machine, slitter rewinder India, coating machine manufacturer, converting machinery, lamination machine, tape manufacturing equipment, packaging machinery India" />
        <link rel="canonical" href="https://heroimpex.com/" />
      </Helmet>
      
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20" />
        
        <div className="container mx-auto px-4 py-20 lg:py-28 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 hover:bg-amber-500/30">
                  <Award className="w-3 h-3 mr-1" />
                  {companyInfo.tagline}
                </Badge>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  Leading Manufacturer of{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                    Coating & Converting Machines in India
                  </span>{" "}
                  Since 1977
                </h1>
                <p className="text-lg text-slate-300 leading-relaxed">
                  <strong className="text-amber-400">Trusted by 800+ manufacturers across 20+ countries.</strong> Expert machining and manufacturing of BOPP tape coating, web lamination, slitting rewinding, and converting machines built to German quality standards. Turnkey solutions with custom engineering.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                  <div className="text-3xl font-bold text-amber-400">{companyInfo.experience}</div>
                  <div className="text-sm text-slate-400 mt-1">Years Experience</div>
                </div>
                <div className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                  <div className="text-3xl font-bold text-amber-400">{companyInfo.customers}</div>
                  <div className="text-sm text-slate-400 mt-1">Happy Clients</div>
                </div>
                <div className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                  <div className="text-3xl font-bold text-amber-400">{companyInfo.countries}</div>
                  <div className="text-sm text-slate-400 mt-1">Countries</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg hover:shadow-xl transition-all group text-base px-8 py-6">
                    🚀 Get FREE Quote Now
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent("Hi, I need a quote for machinery")}`} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white border-0">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp Quote
                  </Button>
                </a>
              </div>

              <div className="flex flex-wrap items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-green-400" />
                  <span className="text-sm">ISO Certified</span>
                </div>
                <div className="flex items-center gap-2">
                  <Factory className="w-5 h-5 text-amber-400" />
                  <span className="text-sm">Made in India</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-400" />
                  <span className="text-sm">Export Quality</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white border-4 border-amber-400">
                {/* Quick Quote Form */}
                <div className="w-full p-8">
                  <div className="text-center mb-6">
                    <div className="inline-block px-4 py-2 bg-amber-500 text-white rounded-full text-sm font-bold mb-3">
                      ⚡ 24 Hour Response Guarantee
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Get FREE Quote Now</h3>
                    <p className="text-slate-600 text-sm">Our experts will contact you within 24 hours</p>
                  </div>
                  
                  <form className="space-y-4">
                    <div>
                      <input
                        type="text"
                        placeholder="Your Name *"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-amber-500 focus:outline-none text-slate-900"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        placeholder="Email Address *"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-amber-500 focus:outline-none text-slate-900"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="tel"
                        placeholder="Phone Number *"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-amber-500 focus:outline-none text-slate-900"
                        required
                      />
                    </div>
                    <div>
                      <select className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-amber-500 focus:outline-none text-slate-900">
                        <option>Select Machine Type</option>
                        <option>BOPP Tape Coating Machine</option>
                        <option>Slitter Rewinder</option>
                        <option>Lamination Machine</option>
                        <option>Converting Machine</option>
                        <option>Other / Custom Requirement</option>
                      </select>
                    </div>
                    <Link to="/contact">
                      <Button type="button" className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-6 text-lg font-bold shadow-xl">
                        🚀 GET FREE QUOTE NOW
                      </Button>
                    </Link>
                  </form>
                  
                  <div className="mt-4 text-center">
                    <p className="text-xs text-slate-500">✓ No spam · ✓ Quick response in 24hrs · ✓ Expert guidance · ✓ Custom solutions</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-amber-500/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-amber-500/20 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4">Our Products</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Industry-Leading Machinery Solutions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Precision-engineered machines for coating, slitting, and converting operations with proven reliability and performance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map((product) => (
              <Card key={product.id} className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="relative overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-amber-600 text-white">{product.category}</Badge>
                  </div>
                </div>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-lg mb-2 text-slate-900 group-hover:text-amber-700 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <Link to="/contact">
                    <Button size="sm" variant="ghost" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 w-full">
                      Request Quote
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/products">
              <Button size="lg" className="bg-slate-900 hover:bg-slate-800">
                View All Products
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4">Why Choose Us</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Your Trusted Manufacturing Partner
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Four decades of excellence in delivering high-quality machinery with unmatched reliability and support.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item) => (
              <Card key={item.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-lg">
                      {item.stat}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2 text-slate-900">{item.title}</h3>
                      <p className="text-sm text-slate-600">{item.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence Map */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4">Global Presence</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Serving 20+ Countries Worldwide
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our machines are trusted by manufacturers across continents, from Asia to Africa, Middle East to Americas.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <WorldMapDetailed countries={globalPresence} />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4">Testimonials</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Trusted by leading manufacturers worldwide for quality, reliability, and exceptional service.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${testimonial.industry}-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <div className="font-semibold text-amber-700">{testimonial.industry}</div>
                    <div className="text-sm text-slate-600">{testimonial.location}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hero Impex YouTube Videos Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-200">Our Machines in Action</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              See Our Manufacturing Excellence
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Watch our advanced coating, lamination, and converting machines in operation. Real demonstrations from Hero Impex manufacturing facility.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <Card className="overflow-hidden hover:shadow-xl transition-all hover:scale-[1.02] border-2 border-amber-100">
              <div className="relative aspect-video bg-slate-100">
                <iframe
                  src="https://www.youtube.com/embed/7W5tC8pfnTw"
                  title="Hero Impex Machine Demo 1"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <CardContent className="p-4 bg-gradient-to-br from-white to-amber-50">
                <h3 className="font-semibold text-slate-900 mb-2">Coating Machine in Action</h3>
                <p className="text-sm text-slate-600">Advanced coating technology demonstration</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-all hover:scale-[1.02] border-2 border-amber-100">
              <div className="relative aspect-video bg-slate-100">
                <iframe
                  src="https://www.youtube.com/embed/H2nlqtH6w_Y"
                  title="Hero Impex Paper Core Cutter"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <CardContent className="p-4 bg-gradient-to-br from-white to-amber-50">
                <h3 className="font-semibold text-slate-900 mb-2">Paper Core Cutter Machine</h3>
                <p className="text-sm text-slate-600">Precision cutting technology</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-all hover:scale-[1.02] border-2 border-amber-100">
              <div className="relative aspect-video bg-slate-100">
                <iframe
                  src="https://www.youtube.com/embed/gKlDgV05dH8"
                  title="Hero Impex Converting Machine"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <CardContent className="p-4 bg-gradient-to-br from-white to-amber-50">
                <h3 className="font-semibold text-slate-900 mb-2">Converting Machine Demo</h3>
                <p className="text-sm text-slate-600">High-speed converting operations</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-all hover:scale-[1.02] border-2 border-amber-100">
              <div className="relative aspect-video bg-slate-100">
                <iframe
                  src="https://www.youtube.com/embed/Z3ts5WCPUyE"
                  title="Hero Impex Slitting Machine"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <CardContent className="p-4 bg-gradient-to-br from-white to-amber-50">
                <h3 className="font-semibold text-slate-900 mb-2">Slitting Rewinder</h3>
                <p className="text-sm text-slate-600">Automated slitting technology</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-all hover:scale-[1.02] border-2 border-amber-100">
              <div className="relative aspect-video bg-slate-100">
                <iframe
                  src="https://www.youtube.com/embed/3Iv7vWMRq9c"
                  title="Hero Impex Lamination Machine"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <CardContent className="p-4 bg-gradient-to-br from-white to-amber-50">
                <h3 className="font-semibold text-slate-900 mb-2">Lamination Machine</h3>
                <p className="text-sm text-slate-600">Multi-layer lamination process</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-all hover:scale-[1.02] border-2 border-amber-100">
              <div className="relative aspect-video bg-slate-100">
                <iframe
                  src="https://www.youtube.com/embed/VryFRS7uafM"
                  title="Hero Impex Production Line"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <CardContent className="p-4 bg-gradient-to-br from-white to-amber-50">
                <h3 className="font-semibold text-slate-900 mb-2">Complete Production Line</h3>
                <p className="text-sm text-slate-600">End-to-end manufacturing solution</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-10">
            <a
              href="https://www.youtube.com/@heroimpexindiapvtltd8994"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg">
                View All Videos on YouTube
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-amber-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Transform Your Production?
            </h2>
            <p className="text-lg text-amber-100 mb-8">
              Get a free consultation and customized quote for your manufacturing requirements. Our experts are ready to help you choose the perfect machinery solution.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-100 shadow-xl">
                  Request Free Quote
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};
